import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFirestore, doc, setDoc, updateDoc, onSnapshot, deleteDoc, deleteField } from 'firebase/firestore';
import { getCurrentUserId, getCurrentUserProfile, getCurrentUserRole } from 'shared/utils/SessionDataUtils';
import { Assessment } from 'modules/assessment/Assessment';
import masterApiCall from 'shared/services/MasterDataApi';
import { useLoading } from 'shared/contexts/Loading';
import apiCall from 'shared/services/ApiAdapter';
import { ProtectedVisibility } from 'shared/security/ProtectedRoute';
import { GenAIWrapper } from 'shared/security/GenAIWrapperProvider';
import ProblemSolverModal from './ProblemSolverModal';
import genAIApiCall from 'shared/services/GenAPIPath';
import { loadMathJaxScript } from './loadMathJaxScript';


// Default placeholder code for students who haven't entered a code yet.
const DEFAULT_STUDENT_LISTEN_CODE = "00000000";

const AILoader = () => {
    const [messageIndex, setMessageIndex] = useState(0);
    const messages = [
        "Dusting off the textbooks...",
        "Consulting the greatest minds...",
        "Crunching numbers and eating pi...",
        "Connecting the brain cells...",
        "Formulating a stroke of genius...",
        "Aha! Almost there...",
        "Warming up the neural networks...",
        "Dividing by zero... just kidding!",
        "Synthesizing knowledge particles...",
        "Searching for the lost variables...",
        "Calculating the speed of light...",
        "Equilibrating chemical equations...",
        "Consulting with Isaac Newton...",
        "Bending the laws of physics...",
        "Finding the lowest common denominator...",
        "Mapping out the logic trees..."
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex(prev => {
                let nextIndex;
                do {
                    nextIndex = Math.floor(Math.random() * messages.length);
                } while (nextIndex === prev);
                return nextIndex;
            });
        }, 5000);
        return () => clearInterval(interval);
    }, [messages.length]);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-sm w-full mx-4 border border-slate-200 dark:border-slate-700">
                <div className="relative mb-6">
                    <div className="absolute inset-0 bg-indigo-500 rounded-full animate-ping opacity-20"></div>
                    <div className="relative bg-gradient-to-tr from-indigo-600 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center shadow-lg shadow-indigo-500/30">
                        <svg className="w-8 h-8 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path></svg>
                    </div>
                </div>

                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2 text-center">
                    AI Guided Solution
                </h3>

                <div className="h-6 flex items-center justify-center overflow-hidden w-full relative">
                    <p key={messageIndex} className="text-sm font-medium text-slate-500 dark:text-slate-400 absolute animate-in slide-in-from-bottom-2 fade-in duration-300">
                        {messages[messageIndex]}
                    </p>
                </div>

                <div className="w-full bg-slate-100 dark:bg-slate-700 h-1.5 rounded-full mt-6 overflow-hidden relative">
                    <div className="absolute top-0 left-0 h-full bg-indigo-500 rounded-full w-1/3 animate-[ai-bounce_1.5s_ease-in-out_infinite_alternate]"></div>
                </div>
            </div>
            <style>{`
                @keyframes ai-bounce {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(200%); }
                }
            `}</style>
        </div>
    );
};

const Icons = {
    Sparkles: () => (
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path></svg>
    )
}

const generateUniqueQuizCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
};

// Helper function to defensively convert Firestore object-arrays back to real arrays
const getQuestionsArray = (questions) => {
    return Array.isArray(questions)
        ? questions
        : Object.values(questions || {});
};

// Helper to extract ONLY the mutable fields (id, active, answeredBy) for writing to Firestore
const getMutableQuestionsState = (questionsArray) => {
    return (questionsArray || [])?.map(q => ({
        id: q.id.toString(),
        active: q.active,
        answeredBy: q.answeredBy || {},
    }));
};

// Helper function to get the correct answer text for a question (Used in presentation components)
const getCorrectAnswerText = (q) => {
    const correctChoice = q.answerChoices?.find(c => c.isCorrect);
    return correctChoice ? correctChoice.choiceText : null;
};

// Helper function to combine question text and details (Used in presentation components)
const getFullQuestionText = (q) => {
    return q.questionText + (q.details?.length > 0
        ? '\n\n' + q.details.map(d => d.questionChoice).join('\n')
        : '');
};

// Helper function to get options array (Used in presentation components)
const getOptionsArray = (q) => {
    return q.answerChoices?.map(c => c.choiceText) || [];
};

const createInitialState = (quizCode, questions, evaluation) => {
    // 1. Full initial question payload includes all content and runtime fields
    const initialQuestionData = questions?.map(q => ({
        ...q,
        id: q.id.toString(),
        active: false,
        answeredBy: {},
    }));

    // 2. Separate the content from the mutable state
    const questionContent = initialQuestionData?.map(q => {
        // Use destructuring to exclude mutable fields
        const { active, answeredBy, ...content } = q;
        return content;
    });

    // 3. Create the minimal, mutable state array
    const questionStates = getMutableQuestionsState(initialQuestionData);

    return {
        quiz: {
            id: evaluation ? evaluation.id : "",
            name: evaluation ? evaluation.name : "",
            eligibility: "Students",
            status: "lobby",
            creator: "Professor Smith",
            joinCode: quizCode,
        },
        questionContent: questionContent ? questionContent : [], // 🆕 Full content stored here initially
        questionStates: questionStates ? questionStates : [], // 🆕 Minimal, mutable state
        joinedStudents: [],
        activeQuestionIndex: -1,
    };
};

export const QuizContext = createContext(null);

// --- Tutor Quiz Wrapper (Shared UI component logic) ---
export const TutorQuizWrapper = ({
    questionsArray, // The full merged array (content + live states) from TutorApp
    liveState,
}) => {

    const { activeQuestionIndex, quiz } = liveState;

    // 1. Find the currently active question
    const activeQuestion = questionsArray[activeQuestionIndex];

    if (!activeQuestion || quiz.status !== 'active') {
        return <p>Error: Active question not found or Quiz is not active.</p>;
    }

    // 2. Format the data for the Assessment component (single question array)
    const currentQuestionData = [{
        ...activeQuestion,
        id: activeQuestion.id,
    }];

    // 3. Define the dummy submission handler (Tutor doesn't submit)
    const handleDummySubmit = () => {
        console.warn("Tutor attempted to submit an answer. Submission disabled.");
    };

    return (
        <Assessment
            questionsData={currentQuestionData}
            key={`tutor-q-${activeQuestion.id}`}
            onExit={() => { /* Tutor should use Next/End buttons */ }}
            onAssessmentSubmit={handleDummySubmit}
            isSubmitting={false}
            submitError={null}
            course={quiz.name}
            evaluation={null}
            enableTimer={false} // Disable the timer display for the Tutor view
            readOnly={true} // ASSUMING Assessment component respects a readOnly prop
            isLiveQuiz={true}
        />
    );
};
// --- MAIN APP COMPONENT ---

/**
 * @param {string} selectedEvaluationId - Not used in this context, but kept for signature consistency.
 * @param {'interactive'|'presentation'} quizMode - Determines if Firebase is used for student interaction.
 */
const MasterQuizApp = ({ selectedEvaluation, quizMode, displayToast }) => {
    const { startLoading, stopLoading } = useLoading();

    const [fetchedQuestions, setFetchedQuestions] = useState(null)
    const [studentInfo, setStudentInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userId, setUserId] = useState(null);
    // Use a placeholder code initially
    const [quizCode, setQuizCode] = useState(DEFAULT_STUDENT_LISTEN_CODE);
    const [liveState, setLiveState] = useState();
    const [firebaseError, setFirebaseError] = useState(null); // New state for Firebase errors

    const userRole = getCurrentUserRole();
    const isInteractiveMode = quizMode === 'interactive';

    const firebaseConfigRef = useRef({});
    const dbRef = useRef(null);
    const authRef = useRef(null);
    const quizDocRef = useRef(null);
    const unsubscribeRef = useRef(null);

    // Initial fetch of questions (runs only once on mount)
    useEffect(() => {
        loadMathJaxScript();
        const init = async () => {
            if (!selectedEvaluation) return;

            let questionCodesList =
                selectedEvaluation?.questionIds || selectedEvaluation?.questionCodes || [];

            // normalize if it's a string
            if (typeof questionCodesList === "string") {
                questionCodesList = questionCodesList
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
            }

            // fetch questions once at init
            if (Array.isArray(questionCodesList) && questionCodesList.length > 0) {
                try {
                    const res = await masterApiCall(
                        "/questions/by-codes",
                        null,
                        "POST",
                        { questionCodes: questionCodesList },
                        startLoading,
                        stopLoading,
                        displayToast
                    );

                    const fetchedQuestionsList = Array.isArray(res)
                        ? res
                        : res?.content ?? [];

                    // Set fetched questions, this triggers the next useEffect for Firebase
                    setFetchedQuestions(fetchedQuestionsList);

                    // Set initial local state with a placeholder code
                    setLiveState(createInitialState(DEFAULT_STUDENT_LISTEN_CODE, fetchedQuestionsList, selectedEvaluation));
                } catch (err) {
                    console.error("Error fetching questions:", err);
                    stopLoading();
                    setIsLoading(false);
                }
            }
        };
        init();
    }, [selectedEvaluation]); // <-- Dependency on selectedEvaluation to fetch data


    // 💡 Firebase Setup/Listener Function is now conditional on isInteractiveMode
    const setupQuizListener = (db, currentQuizCode, fetchedQuestions) => {
        if (!isInteractiveMode) {
            console.log("Presentation Mode: Skipping Firebase listener setup.");
            setIsLoading(false);
            return;
        }

        // Safety check for AppId before using it
        if (!firebaseConfigRef.current.appId) {
            console.error("Firebase App ID missing, cannot set up quiz listener.");
            return;
        }

        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }

        const docPath = `/artifacts/${firebaseConfigRef.current.appId}/public/data/quiz/${currentQuizCode}`;
        quizDocRef.current = doc(db, docPath);

        const unsubscribe = onSnapshot(quizDocRef.current, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();

                const questionsArray = getQuestionsArray(data.questionStates);

                const mutableQuestionsState = questionsArray.map(q => ({
                    id: q.id,
                    active: q.active,
                    answeredBy: q.answeredBy,
                }));

                setLiveState({
                    quiz: data.quiz,
                    joinedStudents: data.joinedStudents,
                    activeQuestionIndex: data.activeQuestionIndex,
                    questionStates: mutableQuestionsState, // Store ONLY mutable subset
                });

            } else {
                setLiveState(createInitialState(currentQuizCode, fetchedQuestions, selectedEvaluation));
            }
            setIsLoading(false);
        }, (error) => {
            console.error("Error listening to quiz state:", error);
            setIsLoading(false);
        });

        unsubscribeRef.current = unsubscribe;
        setQuizCode(currentQuizCode);
    };

    // 💡 Firebase Function is now conditional on isInteractiveMode
    const deleteCurrentQuizInstance = async () => {
        if (!isInteractiveMode || !quizDocRef.current) return;
        try {
            await deleteDoc(quizDocRef.current);
            console.log(`Quiz document deleted: ${liveState.quiz.joinCode}`);
        } catch (error) {
            console.error("Error deleting quiz document:", error);
        }
    };

    // 💡 Firebase Function is now conditional on isInteractiveMode
    const startNewQuizInstance = async () => {
        if (!fetchedQuestions || !dbRef.current || !isInteractiveMode) {
            // This is handled by initFirebase for the initial run.
            return;
        }

        const newCode = generateUniqueQuizCode();
        const db = dbRef.current;
        const newInitialState = createInitialState(newCode, fetchedQuestions, selectedEvaluation);

        // Update local state immediately for fast response
        setLiveState(newInitialState);

        const newDocRef = doc(db, `/artifacts/${firebaseConfigRef.current.appId}/public/data/quiz/${newCode}`);

        // 🚨 IMPORTANT: Write the FULL data initially
        console.log(newInitialState)
        await setDoc(newDocRef, newInitialState);

        // SETUP LISTENER *AFTER* DOCUMENT IS CREATED/CONFIGURED
        setupQuizListener(db, newCode, fetchedQuestions);
    }

    // 💡 Firebase initialization Effect - Only runs if in Interactive Mode AND questions are fetched
    useEffect(() => {
        if (!isInteractiveMode) {
            if (userRole === 'ROLE_TEACHER' && fetchedQuestions) {
                setIsLoading(false);
            }
            return;
        }

        // Only proceed if fetchedQuestions are available and Firebase is not already initialized
        if (!fetchedQuestions || authRef.current) {
            return;
        }

        const initFirebase = async () => {
            try {
                setFirebaseError(null); // Clear previous errors
                startLoading(); // Ensure loading is active for the Firebase part

                // 1. FETCH FIREBASE CONFIG (ASYNC)
                const url = `/config/firebase`;
                const data = await apiCall(
                    url,
                    null,
                    "GET",
                    null,
                    // FIX: Pass null for loading functions as we handle them globally here
                    startLoading,
                    stopLoading,
                    displayToast
                );

                // 2. INITIALIZE FIREBASE APP *AFTER* CONFIG IS FETCHED
                const config = data;
                firebaseConfigRef.current = config;

                const app = initializeApp(firebaseConfigRef.current);
                const db = getFirestore(app);
                const auth = getAuth(app);
                dbRef.current = db;
                authRef.current = auth;
                // 4. AUTHENTICATE USER
                await signInAnonymously(auth);
                const currentUserId = getCurrentUserId();
                setUserId(currentUserId);

                // 3. START NEW QUIZ INSTANCE (CREATES DOCUMENT AND SETS UP LISTENER)
                // This will generate the unique code
                await startNewQuizInstance();

            } catch (error) {
                console.error("Failed to initialize Firebase or fetch data:", error);

                // **FIX: Capture and display error**
                setFirebaseError("Failed to start live quiz session. Please ensure your Firebase configuration is correct and try again.");
                displayToast('Firebase initialization failed. Interactive Mode disabled.', 'error');

            } finally {
                stopLoading();
                setIsLoading(false);
            }
        };

        initFirebase();


        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, [isInteractiveMode, fetchedQuestions]); // Dependency on fetchedQuestions ensures it runs at the right time.

    // 💡 Firebase Function is now conditional on isInteractiveMode
    const updateQuizState = async (updates) => {
        if (!isInteractiveMode || !quizDocRef.current) {
            if (isInteractiveMode) {
                console.error("Firestore document reference is not available. Have you joined/started a quiz?");
            } else {
                console.log("Presentation Mode: Skipping Firestore update.");
            }
            return;
        }
        try {
            await updateDoc(quizDocRef.current, updates);
        } catch (error) {
            console.error("Failed to update quiz state in Firestore:", error);
        }
    };

    // Helper now merges static RAW_QUESTIONS_JSON content with mutable live state
    const getFullQuestionState = (liveState, fetchedQuestions) => {
        const liveQuestionStates = liveState?.questionStates;
        const liveMutableQs = getQuestionsArray(liveQuestionStates);

        // Merge static immutable content with live mutable data
        return fetchedQuestions?.map(staticQ => {
            const liveQ = liveMutableQs.find(lq => lq.id.toString() === staticQ.id.toString());
            return {
                ...staticQ, // Full immutable content (Local source of truth)
                // Override/add mutable state from Firestore/Local state (Live source)
                answeredBy: liveQ?.answeredBy || {},
                active: liveQ?.active || false,
            };
        });
    };

    // 💡 Combined Local and Firebase update logic
    const updateLiveStateAndFirestore = async (updates, localUpdates) => {
        // 1. Update Firestore (Only in Interactive Mode)
        await updateQuizState(updates);

        // 2. Update Local State Optimistically (Always)
        setLiveState(prevState => {
            let newState = { ...prevState };

            if (localUpdates['quiz.status']) {
                newState.quiz = { ...newState.quiz, status: localUpdates['quiz.status'] };
            }
            if (localUpdates['activeQuestionIndex'] !== undefined) {
                newState.activeQuestionIndex = localUpdates['activeQuestionIndex'];
            }
            if (localUpdates['questionStates']) {
                newState.questionStates = localUpdates['questionStates'];
            }
            if (localUpdates['joinedStudents']) {
                newState.joinedStudents = localUpdates['joinedStudents'];
            }

            return newState;
        });
    };


    const renderView = (fetchedQuestions) => {
        if (isLoading && userRole === 'ROLE_TEACHER') {
            return (
                <div className="flex justify-center items-center h-64">
                    <svg className="animate-spin h-12 w-12 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            );
        }

        // Display Firebase Error in Interactive Mode
        if (isInteractiveMode && firebaseError) {
            return (
                <div className="text-center p-8 bg-rose-100 border-2 border-rose-500 rounded-lg">
                    <h2 className="text-2xl font-bold text-rose-700 mb-4">Error Setting Up Live Quiz</h2>
                    <p className="text-lg text-rose-600">{firebaseError}</p>
                    <p className="mt-4 text-gray-600">Please check the console for details or switch to Presentation Mode.</p>
                </div>
            );
        }

        if (userRole === 'ROLE_TEACHER') {
            const tutorProps = {
                quizMode,
                updateLiveStateAndFirestore,
                startNewQuizInstance,
                deleteCurrentQuizInstance,
                questionContent: fetchedQuestions,
                getFullQuestionState: () => getFullQuestionState(liveState, fetchedQuestions),
                startLoading,
                stopLoading,
                displayToast
            };

            // 💡 CRITICAL CHANGE: Select the component based on mode
            if (quizMode === 'interactive') {
                return <TutorInteractiveApp {...tutorProps} />;
            } else {
                return <TutorPresentationApp {...tutorProps} />;
            }
        }
        // TODO: Render Student App for student roles
        return <div>Student View Not Implemented Here (Mode: {quizMode})</div>;
    };

    return (
        <QuizContext.Provider value={{ liveState, updateQuizState: updateLiveStateAndFirestore, userId, dbRef, studentInfo, setLiveState, isInteractiveMode, fetchedQuestions }}>
            <div className="text-gray-800 min-h-screen font-sans flex flex-col items-center p-4">
                <div className="w-full p-6 md:p-10">
                    {renderView(fetchedQuestions)}
                </div>
            </div>
        </QuizContext.Provider>
    );
};
export default MasterQuizApp;

// ---------------------------------------------
// ## 👨‍🏫 TutorInteractiveApp (Firebase Heavy)
// ---------------------------------------------

const TutorInteractiveApp = ({ startNewQuizInstance, deleteCurrentQuizInstance, getFullQuestionState, updateLiveStateAndFirestore,
    quizMode, startLoading, stopLoading, displayToast }) => {
    const { liveState } = useContext(QuizContext);
    const { quiz } = liveState;

    const questionsArray = getFullQuestionState();

    const handleStart = async () => {
        if (quiz.status !== 'lobby') return;
        const currentQuestions = questionsArray;
        if (currentQuestions?.length === 0) return;

        const updatedQuestions = currentQuestions?.map((q, index) => ({
            ...q,
            active: index === 0,
        }));
        const questionsToWrite = getMutableQuestionsState(updatedQuestions);

        // Interactive mode: Delete content and write mutable state
        const firestoreUpdates = {
            'quiz.status': 'active',
            'activeQuestionIndex': 0,
            'questionStates': questionsToWrite,
            'questionContent': deleteField(),
        };
        const localUpdates = { 'quiz.status': 'active', 'activeQuestionIndex': 0, 'questionStates': questionsToWrite };

        try {
            await updateLiveStateAndFirestore(firestoreUpdates, localUpdates);
            console.log("Interactive Quiz started.");
        } catch (error) {
            console.error("Error starting interactive quiz:", error);
        }
    };

    const handleNext = async () => {
        const nextIndex = liveState.activeQuestionIndex + 1;
        const currentQuestions = questionsArray;

        if (nextIndex < currentQuestions?.length) {
            const updatedQuestions = currentQuestions?.map((q, index) => {
                if (index === liveState.activeQuestionIndex) {
                    return { ...q, active: false };
                }
                if (index === nextIndex) {
                    // Reset answers for the new question in interactive mode
                    return { ...q, active: true, answeredBy: {} };
                }
                return q;
            });

            const questionsToWrite = getMutableQuestionsState(updatedQuestions);

            // Interactive mode: Update state and index
            const firestoreUpdates = { 'activeQuestionIndex': nextIndex, 'questionStates': questionsToWrite };
            const localUpdates = { 'activeQuestionIndex': nextIndex, 'questionStates': questionsToWrite };

            try {
                await updateLiveStateAndFirestore(firestoreUpdates, localUpdates);
            } catch (error) {
                console.error("Error advancing interactive question:", error);
            }
        } else {
            handleEnd();
        }
    };

    const handleEnd = async () => {
        const currentQuestions = questionsArray;
        const currentQuestionIndex = liveState.activeQuestionIndex;
        const updatedQuestions = currentQuestions.map((q, index) => (index === currentQuestionIndex ? { ...q, active: false } : q));
        const questionsToWrite = getMutableQuestionsState(updatedQuestions);

        const firestoreUpdates = { 'quiz.status': 'finished', 'questionStates': questionsToWrite };
        const localUpdates = { 'quiz.status': 'finished', 'questionStates': questionsToWrite };

        try {
            await updateLiveStateAndFirestore(firestoreUpdates, localUpdates);
        } catch (error) {
            console.error("Error ending interactive quiz:", error);
        }
    };

    const handleRestart = async () => {
        await deleteCurrentQuizInstance();
        await startNewQuizInstance();
    };

    const mergedLiveState = { ...liveState, questions: questionsArray };
    const quizStatus = questionsArray?.length === 0 ? 'loading' : quiz.status;

    const renderScreen = () => {
        switch (quizStatus) {
            case 'lobby':
                return <TutorLobbyScreen quiz={quiz} onStart={handleStart} quizMode={quizMode} />;
            case 'active':
                return <TutorLiveQuizScreen liveState={mergedLiveState} onNext={handleNext} onEnd={handleEnd} quizMode={quizMode}
                    displayToast={displayToast} startLoading={startLoading} stopLoading={stopLoading} />;
            case 'finished':
                return <TutorResultsScreen liveState={mergedLiveState} onRestart={handleRestart} quizMode={quizMode} />;
            default:
                return <div>Initializing Interactive Quiz Data...</div>;
        }
    };

    return (
        <div className="flex flex-col items-center space-y-8">
            {renderScreen()}
        </div>
    );
};

// ---------------------------------------------
// ## 📺 TutorPresentationApp (Local Only)
// ---------------------------------------------

const TutorPresentationApp = ({ getFullQuestionState, updateLiveStateAndFirestore, quizMode, startLoading, stopLoading, displayToast }) => {
    const { liveState, setLiveState, fetchedQuestions } = useContext(QuizContext);
    const quiz = liveState?.quiz;

    const questionsArray = getFullQuestionState();

    const handleStart = async () => {
        if (quiz.status !== 'lobby') return;
        if (questionsArray.length === 0) return;

        const updatedQuestions = questionsArray.map((q, index) => ({
            ...q,
            active: index === 0,
        }));
        const questionsToWrite = getMutableQuestionsState(updatedQuestions);

        // Presentation mode: Local updates only (Firestore updates object is empty)
        const localUpdates = { 'quiz.status': 'active', 'activeQuestionIndex': 0, 'questionStates': questionsToWrite };

        await updateLiveStateAndFirestore({}, localUpdates);
        console.log("Presentation Quiz started locally.");
    };

    const handleNext = async () => {
        const nextIndex = liveState.activeQuestionIndex + 1;
        const currentQuestions = questionsArray;

        if (nextIndex < currentQuestions.length) {
            const updatedQuestions = currentQuestions.map((q, index) => {
                if (index === liveState.activeQuestionIndex) {
                    return { ...q, active: false };
                }
                if (index === nextIndex) {
                    // Do NOT reset answers in presentation mode
                    return { ...q, active: true };
                }
                return q;
            });

            const questionsToWrite = getMutableQuestionsState(updatedQuestions);

            // Presentation mode: Local updates only
            const localUpdates = { 'activeQuestionIndex': nextIndex, 'questionStates': questionsToWrite };
            await updateLiveStateAndFirestore({}, localUpdates);
        } else {
            handleEnd();
        }
    };

    const handleEnd = async () => {
        const currentQuestions = questionsArray;
        const currentQuestionIndex = liveState.activeQuestionIndex;
        const updatedQuestions = currentQuestions.map((q, index) => (index === currentQuestionIndex ? { ...q, active: false } : q));
        const questionsToWrite = getMutableQuestionsState(updatedQuestions);

        // Presentation mode: Local updates only
        const localUpdates = { 'quiz.status': 'finished', 'questionStates': questionsToWrite };
        await updateLiveStateAndFirestore({}, localUpdates);
    };

    const handleRestart = async () => {
        // Just reset local state to the initial state
        const newInitialState = createInitialState(DEFAULT_STUDENT_LISTEN_CODE, fetchedQuestions);

        // Update local state directly
        setLiveState(newInitialState);
    };

    const mergedLiveState = { ...liveState, questions: questionsArray };
    const quizStatus = questionsArray?.length === 0 ? 'loading' : quiz?.status;

    const renderScreen = () => {
        switch (quizStatus) {
            case 'lobby':
                return <TutorLobbyScreen quiz={quiz} onStart={handleStart} quizMode={quizMode} />;
            case 'active':
                return <TutorLiveQuizScreen liveState={mergedLiveState} onNext={handleNext} onEnd={handleEnd} quizMode={quizMode}
                    startLoading={startLoading} stopLoading={stopLoading} displayToast={displayToast} />;
            case 'finished':
                return <TutorResultsScreen liveState={mergedLiveState} onRestart={handleRestart} quizMode={quizMode} />;
            default:
                return <div>Initializing Presentation Quiz Data...</div>;
        }
    };

    return (
        <div className="flex flex-col items-center space-y-8">
            {renderScreen()}
        </div>
    );
};

// ---------------------------------------------
// ## 🎨 Shared UI Components
// ---------------------------------------------

const TutorLobbyScreen = ({ quiz, onStart, quizMode }) => {

    // ✅ FIX: Call useContext unconditionally at the top level
    const { liveState } = useContext(QuizContext);
    const tutor = getCurrentUserProfile();
    // Use the data conditionally below
    const joinedStudents = quizMode === 'interactive' ? liveState?.joinedStudents || [] : [];
    const maxStudents = 10;

    const joinCodeDisplay = quiz.joinCode.slice(0, 4) + '-' + quiz.joinCode.slice(4);
    const canStart = quiz.status === 'lobby';

    return (
        <div className="flex flex-col w-full space-y-8 animate-fade-in  text-gray-900 dark:text-gray-100 min-h-screen">
            <div className="text-center space-y-2">
                <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">🚀 {quiz.name}</h1>
                <p className="text-lg text-gray-700 dark:text-gray-400">Quiz Administrator: {tutor.firstName} {tutor.lastName}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 w-full mt-4 min-h-[500px]">
                {quizMode === 'interactive' ? (
                    <div className="w-full lg:w-3/4 flex flex-col items-center space-y-8">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl text-center w-full max-w-xl border border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-4">Session Code:</h2>
                            <div className="font-mono text-6xl font-extrabold tracking-wider my-6 bg-gray-800 dark:bg-gray-900 rounded-xl p-5 text-white dark:text-green-400 select-all shadow-inner border border-gray-800 dark:border-gray-700">
                                {joinCodeDisplay}
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">Share this code with your students to allow them to join this session.</p>
                        </div>
                        <button
                            onClick={onStart}
                            disabled={!canStart}
                            className={`w-full max-w-xs px-10 py-4 text-xl font-bold rounded-full transition-all duration-300 shadow-2xl 
                                ${canStart
                                    ? 'bg-gray-700 text-white hover:bg-gray-900 hover:scale-[1.02] dark:bg-green-600 dark:hover:bg-green-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                                }`}
                        >
                            Start Quiz!
                        </button>
                    </div>
                ) : (
                    <div className="w-full lg:w-4/4 flex flex-col items-center space-y-8">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl text-center w-full max-w-xl border border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-4">Presentation Mode Active</h2>
                            <p className="text-lg text-gray-500 dark:text-gray-400 mb-6">
                                you can now share you screen and start the quiz.</p>
                        </div>
                        <button
                            onClick={onStart}
                            disabled={!canStart}
                            className={`w-full max-w-xs px-10 py-4 text-xl font-bold rounded-full transition-all duration-300 shadow-2xl 
                                ${canStart
                                    ? 'bg-gray-700 text-white hover:bg-gray-900 hover:scale-[1.02] dark:bg-green-600 dark:hover:bg-green-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                                }`}
                        >
                            Start Quiz!
                        </button>
                    </div>
                )}




                {quizMode === 'interactive' && (
                    <div className="w-full lg:w-1/4 flex flex-col space-y-4">
                        <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-4 shadow-md border border-gray-300 dark:border-gray-700 flex-grow">
                            <h2 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-3 text-center">
                                Participants ({joinedStudents.length}/{maxStudents})
                            </h2>
                            <ul className="bg-white dark:bg-gray-900 rounded-xl p-3 space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar border border-gray-200 dark:border-gray-700">
                                {joinedStudents.map(student => (
                                    <li key={student.id} className="text-base font-medium bg-gray-200 dark:bg-gray-700 p-2 rounded-lg shadow-sm text-gray-800 dark:text-gray-100">
                                        ✅ {student.name}
                                    </li>
                                ))}
                                {joinedStudents.length === 0 && <li className="text-base text-gray-500 dark:text-gray-500 p-2 text-center">Waiting for students...</li>}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

const TutorLiveQuizScreen = ({ liveState, onNext, onEnd, quizMode, startLoading, stopLoading, displayToast }) => {

    const questionsArray = liveState.questions;
    const { activeQuestionIndex, joinedStudents } = liveState;
    const [timer, setTimer] = useState(180);
    const [answerRevealed, setAnswerRevealed] = useState(false);
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    const [workflowResult, setWorkflowResult] = useState(null);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const isInteractive = quizMode === 'interactive';

    useEffect(() => {
        setTimer(180);
        setAnswerRevealed(false);

        const timerInterval = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timerInterval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timerInterval);
    }, [activeQuestionIndex, isInteractive]);

    const currentQuestion = questionsArray[activeQuestionIndex];

    if (!currentQuestion) {
        return <div className="text-xl font-medium text-center p-8 text-gray-600">Loading current question details...</div>;
    }
    const answeredCount = isInteractive ? Object.keys(currentQuestion.answeredBy || {}).length : 0;
    const totalStudents = isInteractive ? joinedStudents.length : 0;

    const isLastQuestion = activeQuestionIndex === questionsArray.length - 1;
    const correctAnswer = getCorrectAnswerText(currentQuestion);

    const handleGuidedSolution = async (forceRefetch = false, _attempt = 1) => {

        try {
            setIsGeneratingAI(true);
            let questionPayload = { ...currentQuestion }; // Create a mutable copy

            // 1. Cache Check
            if (!forceRefetch && currentQuestion.solution && _attempt === 1) {
                let isCacheValid = false;
                try {
                    const parsed = typeof currentQuestion.solution === 'string' ? JSON.parse(currentQuestion.solution) : currentQuestion.solution;
                    const payload = parsed.result ? (typeof parsed.result === 'string' ? JSON.parse(parsed.result) : parsed.result) : parsed;
                    const finalObj = typeof payload === 'string' ? JSON.parse(payload) : payload;

                    if (finalObj?.finalSolution || finalObj?.solution) {
                        isCacheValid = true;
                    }
                } catch (e) { }

                if (isCacheValid) {
                    setWorkflowResult({ workflowData: currentQuestion.solution, questionRequest: currentQuestion });
                    setShowWorkflowModal(true);
                    setIsGeneratingAI(false);
                    return;
                } else {
                    console.warn("Cached solution missing final solution, fetching new one...");
                }
            }

            if (currentQuestion.pictureUrl && currentQuestion.pictureUrl.trim() !== "") {
                try {
                    // Construct an absolute URL for the image, resolving against the current origin.
                    const imageUrl = new URL(`images/question/${currentQuestion.pictureUrl}`, window.location.origin).href;
                    const response = await fetch(imageUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch image: ${response.statusText}`);
                    }
                    const blob = await response.blob();

                    const base64Image = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                    questionPayload.base64Image = base64Image; // Add the base64 image to the payload
                } catch (imageError) {
                    console.warn("Could not fetch or encode image for AI analysis:", imageError);
                    displayToast({ type: "warning", message: "Could not include question image in AI analysis." });
                }
            }

            const submitData = await genAIApiCall("/workflows/submit/question-metadata-workflow",
                'POST', JSON.stringify(questionPayload), null, null, displayToast);

            if (!submitData.jobId) {
                throw new Error("No Job ID returned");
            }

            let resultData = null;
            for (let i = 0; i < 15; i++) {
                await delay(1000);
                // try {
                const statusData = await genAIApiCall(`/workflows/status/${submitData.jobId}`,
                    'GET', null, null, null, displayToast);

                if (statusData && statusData.status === 'FAILED') {
                    console.log(statusData.result);
                    throw new Error("Failed to execute due to quota or model not found");
                }
                if (statusData && statusData.status === 'COMPLETED') {
                    resultData = statusData;
                    break;
                }
            }

            if (resultData) {
                const finalPayload = resultData.result ? resultData.result : resultData;

                let parsedPayload;
                try {
                    parsedPayload = typeof finalPayload === 'string' ? JSON.parse(finalPayload) : finalPayload;
                } catch (e) {
                    parsedPayload = finalPayload;
                }

                if (!parsedPayload?.finalSolution && !parsedPayload?.solution) {
                    if (_attempt < 2) {
                        console.warn(`AI missing final solution, retrying (Attempt ${_attempt + 1})...`);
                        return await handleGuidedSolution(true, _attempt + 1);
                    } else {
                        throw new Error("AI failed to provide a final solution after retries.");
                    }
                }

                console.log({ workflowData: finalPayload, questionRequest: currentQuestion })
                setWorkflowResult({ workflowData: finalPayload, questionRequest: currentQuestion });

                // 2. Cache Saving
                try {
                    const stringPayload = typeof finalPayload === 'string' ? finalPayload : JSON.stringify(finalPayload);
                    const patchPayload = { id: currentQuestion.id, solution: stringPayload };
                    await masterApiCall(`/questions/${currentQuestion.id}`, null, 'PATCH', patchPayload, null, null, displayToast);
                    // Update locally to avoid future refetches in the same session
                    currentQuestion.solution = stringPayload;
                } catch (saveErr) {
                    console.error("Failed to save AI JSON against question:", saveErr);
                }

                setShowWorkflowModal(true);
            } else {
                throw new Error("Polling timed out without completion");
            }

        } catch (err) {
            console.error("AI Generation Error:", err);
            displayToast({ type: "error", message: "AI analysis cannot be generated at this time." });
        } finally {
            setIsGeneratingAI(false);
        }
    };

    return (
        <div className="flex flex-col w-full space-y-6 animate-fade-in relative">
            {isGeneratingAI && <AILoader />}

            <h2 className="text-2xl font-bold text-gray-700 text-center">Question {activeQuestionIndex + 1} of {questionsArray.length}


                <GenAIWrapper>
                    <ProblemSolverModal
                        isOpen={showWorkflowModal}
                        onClose={() => setShowWorkflowModal(false)}
                        // onMerge={handleModalMerge}
                        data={workflowResult}
                    />
                    <ProtectedVisibility requiredRoles={["ROLE_TEACHER"]} beta={true}>
                        {/* Added 'relative' and 'inline-flex' to the wrapper div */}
                        <div className="relative inline-flex group float-right">

                            {/* Workflow Trigger Button */}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleGuidedSolution(false)}
                                    // Added 'pr-8' (padding-right) to make space for the tag so text doesn't overlap
                                    className="inline-flex items-center px-5 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg hover:border-indigo-400 hover:text-indigo-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:border-indigo-500"
                                >
                                    <span className="p-1 bg-indigo-50 rounded text-indigo-600 mr-2 group-hover:bg-indigo-100 transition-colors dark:bg-slate-700 dark:text-indigo-400">
                                        <Icons.Sparkles />
                                    </span>
                                    <span>Guided Solution with AI</span>
                                </button>
                                {currentQuestion.solution && (
                                    <button
                                        type="button"
                                        onClick={() => handleGuidedSolution(true)}
                                        className="inline-flex items-center px-3 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg hover:border-indigo-400 hover:text-indigo-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:border-indigo-500"
                                        title="Refetch AI Solution"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                    </button>
                                )}
                            </div>

                            {/* Beta Badge */}
                            <span
                                className="absolute -top-3 -right-3 px-2 py-0.5 text-[10px] font-bold tracking-wide
                     text-gray-800 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300
                            shadow-sm transform rotate-12 z-10"
                            >
                                beta
                            </span>
                        </div>
                    </ProtectedVisibility>
                </GenAIWrapper>
            </h2>


            <div className="flex flex-col lg:flex-row gap-4 w-full">

                <div className="w-full lg:w-3/4">
                    <TutorQuizWrapper
                        questionsArray={questionsArray}
                        liveState={liveState}
                    />
                </div>

                <div className="w-full lg:w-1/4 flex flex-col gap-4">

                    <div className="bg-gray-100 rounded-xl p-4 text-center shadow-md border border-gray-300">
                        <p className="text-lg font-semibold text-gray-600">Time Remaining</p>
                        <p className="text-4xl font-extrabold text-gray-800 mt-1">{timer}s</p>
                    </div>

                    {isInteractive && (
                        <div className="bg-gray-100 rounded-xl p-4 shadow-md border border-gray-300">
                            <div className="flex justify-between items-center mb-2 text-sm font-medium text-gray-700">
                                <span>Answers Received:</span>
                                <span>{totalStudents > 0 ? `${answeredCount} / ${totalStudents}` : '0 / 0'}</span>
                            </div>
                            <div className="w-full bg-gray-300 rounded-full h-3 overflow-hidden shadow-inner">
                                <div
                                    className="h-full bg-gray-600 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${totalStudents > 0 ? (answeredCount / totalStudents) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    <div className="w-full">
                        {answerRevealed ? (
                            <div className="bg-gray-200 rounded-xl p-4 text-center border-2 border-gray-500">
                                <p className="font-bold text-gray-700 text-sm mb-1">Correct Answer:</p>
                                <p className="font-extrabold text-gray-900">{correctAnswer}</p>
                            </div>
                        ) : (
                            <button
                                onClick={() => setAnswerRevealed(true)}
                                className="w-full px-4 py-3 font-bold text-white rounded-xl bg-gray-600 hover:bg-gray-700 transition-colors shadow-md"
                            >
                                Reveal Answer
                            </button>
                        )}


                    </div>

                    <div className="flex flex-col space-y-1 pt-2 border-t border-gray-300 mt-auto">
                        <button
                            onClick={onNext}
                            className="px-6 py-3 font-bold text-white rounded-full bg-gray-700 hover:bg-gray-800 transition-colors shadow-lg"
                        >
                            {isLastQuestion ? 'View Results' : 'Next Question'}
                        </button>
                        <button
                            onClick={onEnd}
                            className="px-6 py-3 font-bold text-white rounded-full bg-gray-500 hover:bg-gray-600 transition-colors shadow-lg"
                        >
                            End Quiz
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
const TutorResultsScreen = ({ liveState, onRestart, quizMode }) => {

    const questionsArray = liveState.questions;
    const joinedStudents = quizMode === 'interactive' ? liveState.joinedStudents : [];
    const isInteractive = quizMode === 'interactive';

    const getCorrectAnswerText = (q) => {
        const correctChoice = q.answerChoices?.find(c => c.isCorrect);
        return correctChoice ? correctChoice.choiceText : null;
    };


    const studentScores = joinedStudents.map(student => {
        let score = 0;
        questionsArray?.forEach(q => {
            const correctAnswer = getCorrectAnswerText(q);
            if (q.answeredBy[student.id] === correctAnswer) {
                score++;
            }
        });
        return { ...student, score };
    }).sort((a, b) => b.score - a.score);

    const hasParticipants = joinedStudents.length > 0;

    return (
        <div className="flex flex-col items-center w-full space-y-8 animate-fade-in">
            <h1 className="text-4xl font-extrabold text-green-600">🎉 Quiz Complete!</h1>

            {isInteractive && hasParticipants ? (
                <>
                    <h2 className="text-2xl font-bold text-gray-600">Final Leaderboard</h2>
                    <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl">
                        <ol className="list-none space-y-4">
                            {studentScores.map((student, index) => (
                                <li
                                    key={student.id}
                                    className={`flex items-center p-3 rounded-lg shadow-md transition-all duration-300 ${index === 0 ? 'bg-amber-400 text-gray-900 font-extrabold scale-105 ring-4 ring-amber-500' : 'bg-gray-200 text-gray-800'
                                        }`}
                                >
                                    <span className="w-10 text-xl font-extrabold text-center">{index + 1}.</span>
                                    <span className="flex-1 text-lg font-medium">{student.name}</span>
                                    <span className="text-xl font-bold">{student.score} / {questionsArray.length} pts</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </>
            ) : (
                <p className="text-xl text-gray-500 mt-4">
                    {isInteractive
                        ? "The quiz ended without any participants joining the session."
                        : "Results are not tracked in Presentation Mode. Restart to review again."
                    }
                </p>
            )}

            {/* <p className="text-lg text-gray-500 mt-4">
                {isInteractive ? "The old quiz document has been marked for **cleanup (deletion)** once you restart!" : "Restart will begin a new local session."}
            </p> */}
            <p
                // onClick={onRestart}
                className="px-8 py-4 text-xl font-bold rounded-full transition-all duration-300 bg-rose-600 text-white shadow-lg hover:bg-rose-500 hover:scale-105"
            >
                Session ends here!
            </p>
        </div>
    );
};